import { find,
  get,
  forEach,
  includes,
  toLower,
  noop,
  startsWith,
  isString,
  isObject } from 'lodash';
import { utils, getConversationColor as innerGetConversationColor, moment } from '@liveperson/lp-vue-components';
import { store, i18n, Logger, sessionManager } from 'vue-infra';
import { initialize } from 'launchdarkly-js-client-sdk';
import linkifyHtml from 'linkify-html';
import * as linkify from 'linkifyjs';
import HtmlSanitizerUtils from 'lp-html-sanitizer/src/HtmlSanitizerUtils';
import { MESSAGE_STATUS_LEVELS, NAMESPACE, PENDO_PRODUCT_AREA } from '@/common/consts';
import Currency from '@/common/currency';
import { replaceSpecificElement } from '@/common/sanitizer';
import pkg from '../../package.json';
import { FEATURES } from '@/store/modules/data/logged-in-user/consts';

/**
 * Function to extract the sub domain from any URL
 * @param url
 * @return {String}
 */
export const getSubDomain = (url) => {
  const domainRegEx = new RegExp(/(http{1}s{0,1}?:\/\/)([^/?]+)(\/?)/gi);
  let matches;
  let domain = null;
  if (startsWith(url, 'http')) {
    matches = domainRegEx.exec(url);
  } else {
    matches = domainRegEx.exec(window.location.href);
  }
  if (matches && matches.length >= 3 && matches[2] !== '') {
    domain = matches[1].toLowerCase() + matches[2].toLowerCase(); // 0 - full match 1- HTTPS 2- domain
  }
  return domain;
};

export const empty = noop;

export const getServiceBaseURI = (serviceName, csdsRecords) => {
  const service = find(csdsRecords, item => item.service === serviceName);
  return service ? service.baseURI : null;
};

export function getFeatureFlagsLD(clientSiteId) {
  return new Promise((resolve, reject) => {
    const accountId = store.getters['session/getValue']('accountId');
    const userId = store.getters['session/getValue']('userId');
    try {
      const user = {
        key: '',
        custom: {
          userId,
          accountId,
        },
      };
      const client = initialize(clientSiteId, user);
      client.on('ready', () => {
        resolve(client.allFlags());
      });
    } catch (exception) {
      reject(exception);
    }
  });
}

export const resolveVersionFromResourceKey = (resource) => {
  const urlResolver = window.LE.urlResolver;
  const apiResources = urlResolver.apiResources;
  const apiResource = get(apiResources, resource);
  return apiResource && apiResource.version;
};

export const getConcatStrings = (values) => {
  let concatValue = '';
  forEach(values, (value) => {
    if (value && value !== 'undefined') {
      concatValue = concatValue ? `${concatValue} ${value}` : value;
    }
  });
  return concatValue;
};

export const filterObject = (collection, predicate) => {
  const result = {};

  forEach(collection, (item, key) => {
    if (predicate(item)) {
      result[key] = item;
    }
  });

  return result;
};

export const convertArrayToMap = (array, idAttr) => {
  const map = {};

  array.forEach((item) => {
    map[item[idAttr]] = item;
  });

  return map;
};

export const getInitials = (str) => {
  const splittedStr = str.split(' ');
  let finalStr = splittedStr[0].charAt(0);
  if (splittedStr.length > 1) {
    // if we have more than 2 words, add the first letter of the second word
    finalStr += splittedStr[1].charAt(0);
  } else if (splittedStr[0].length > 1) {
    // if we have only one word, and it has more than 2 characters, add the second character
    finalStr += splittedStr[0].charAt(1);
  }
  return finalStr.toUpperCase();
};

/**
 * @param {Number} date
 * @param {Boolean} [dateOnly]
 * @returns {String}
 */
export const buildCalendarDateNative = (date, dateOnly) => {
  const base = { day: '2-digit', month: 'short', year: 'numeric' }
  const hours = dateOnly === true ? {} : { hour: 'numeric', minute: 'numeric', hour12: true };
  const dateStringSplit = new Date(date).toLocaleString('en-US', { ...base, ...hours })
    .replaceAll(',', '')
    .split(' ');

  return [dateStringSplit[1], dateStringSplit[0], ...dateStringSplit.splice(2)].join(' ');
}

/**
 * @param {Object} props
 * @param {Object} props.fromDate
 * @param {Object} props.toDate
 * @param {Boolean} props.isToDateNowTimeSelected
 * @param {String} props.captionPrefix
 * @param {Boolean} [props.strictDateFormat]
 * @param {Boolean} [props.dateOnly]
 * @returns {String}
 */
export const getDateRangeInfo = (props = {}) => {
  // Set `DD MMM YYYY LT` format for dates older than 48 hours
  // `dateOnly = true` forces this format DD MMM YYYY
  // Apparently, there's also a third format produced by fromDate.calendar() below: MM/DD/YYY
  const getDateString = (momentDate) => moment().diff(momentDate, 'hours') < 48 && !props.strictDateFormat
    ? momentDate.calendar()
    : buildCalendarDateNative(Number(momentDate), props?.dateOnly);
  const fromDateDisplay = getDateString(props.fromDate);
  const toDateDisplay = props.isToDateNowTimeSelected
    ? i18n.t(`aw:${props.captionPrefix}_filter_by_time_to_now_caption`)
    : getDateString(props.toDate);

  return `${fromDateDisplay} - ${toDateDisplay}`;
};

/**
 * Get Client Time according to the format used by the Logger:
 * YYYY-MM-DD HH:mm:ss,SSS
 *
 * @return {string}
 */
export function getLoggerClientTime() {
  return moment().format('YYYY-MM-DD HH:mm:ss,SSS');
}

export function getTimeFromMilliseconds(milliseconds) {
  return milliseconds ? moment(milliseconds).format('hh:mm a') : null;
}

export function isNumeric(value) {
  return !Number.isNaN(parseFloat(value));
}

export const isEmpty = (value) => {
  const s = value || '';
  return s.toString().length === 0;
};

export const getPriceWithCurrency = (amount, currencyCode) => {
  let moneyWithCurrency;
  if (amount) {
    const relevantCurrencyCode = currencyCode ||
      sessionManager.getAccountSettingValueByID(sessionManager.ACCOUNT_SETTINGS_ID.CURRENCY, Currency.defaultCurrency);
    const currencySymbol = get(Currency, ['currencyCodes', relevantCurrencyCode, 'symbol']);
    const separatorSpace = currencySymbol && currencySymbol.length === 1 ? '' : ' ';
    moneyWithCurrency = currencySymbol ? `${currencySymbol}${separatorSpace}${amount}` : `${amount} ${currencyCode}`;
  }
  return moneyWithCurrency;
};

export const generateUUID = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  const r = Math.random() * 16 | 0;
  const v = c === 'x' ? r : (r & 0x3 | 0x8);
  return v.toString(16);
});

/**
 * Trasform a given set of charcters into a hash number
 * @param {*} value
 * @returns {String}
 */
export const toHashCode = utils.toHashCode;
export const detectTextDirection = utils.detectTextDirection;
export const getConversationColor = innerGetConversationColor;

export const isIE = () => {
  let result = false;
  const ua = window.navigator.userAgent;
  const msie = ua.indexOf('MSIE ');

  if (msie > 0 || !!ua.match(/Trident.*rv:11\./)) {
    result = true;
  }
  return result;
};

export const isEdge = () => {
  const navigator = window.navigator;
  return navigator && ((navigator.appName === 'Netscape') &&
    (navigator.userAgent && includes(navigator.userAgent, 'Edge/')));
};

export const isFirefox = () => includes(toLower(navigator.userAgent), 'firefox');

export const loadScripts = async (array) => {
  const scriptTagGenerator = (src, handler, errorHandler) => {
    const script = document.createElement('script');
    script.src = src;

    script.onload = script.onreadystatechange = () => {
      script.onreadystatechange = script.onload = null;
      handler();
    };

    script.onerror = (e) => {
      errorHandler(e);
    };

    const head = document.getElementsByTagName('head')[0];
    (head || document.body).appendChild(script);
  };

  return new Promise((resolve, reject) => {
    const run = () => {
      if (array.length !== 0) {
        scriptTagGenerator(array.shift(), run, reject);
      } else {
        resolve(0);
      }
    };
    run();
  });
};

export const getTheme = currentActiveTheme => (isString(currentActiveTheme) ? currentActiveTheme : get(currentActiveTheme, 'keyName'));

export const getStringified = data => (isObject(data) ? JSON.stringify(data) : data);

export function shouldUpdateMessageStatus(newStatus, oldStatus) {
  return MESSAGE_STATUS_LEVELS[newStatus] &&
    (!MESSAGE_STATUS_LEVELS[oldStatus] || MESSAGE_STATUS_LEVELS[oldStatus] < MESSAGE_STATUS_LEVELS[newStatus]);
}

export function parseSdeCappingSettings(settings) {
  const parsed = [];
  forEach(settings, (setting, sdeKey) => {
    let sdeSetting = setting.quantity ? `:${setting.quantity}` : '';
    sdeSetting += setting.order ? `:${setting.order}` : '';
    if (sdeSetting) {
      parsed.push(sdeKey + sdeSetting);
    }
  });

  return parsed.join(',');
}

/**
 * This function is utilized to increase the sequence number of the last message from UMS,
 * allowing us to accurately represent the sequence of an upcoming message before it's sent.
 * Should get a parameter in the format of 'XXXX:X', first part is the dialogId, second part is the sequence.
 * @returns {*|string}
 */
export function incrementLastMessageId(str) {
  const parts = str.split(':');
  if (parts.length === 2) {
    // Convert the second part to a number, decrement it, and then convert it back to a string
    const numberPart = parseInt(parts[1], 10) + 1;
    // Reassemble the string with the modified number
    return `${parts[0]}:${numberPart}`;
  } else {
    return str;
  }
}

export function copyMessage(message) {
  const { clipboard: clipboardAPI } = navigator;
  const execCommandAPI = document.execCommand;
  const isExecCopySupported = execCommandAPI && document.execCommand('copy');

  let retValue;
  try {
    if (clipboardAPI) {
      clipboardAPI.writeText(message).then(() => {
        retValue = Promise.resolve();
      });
    } else if (isExecCopySupported) {
      const el = document.createElement('textarea');
      el.value = message;
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      retValue = Promise.resolve();
    }
  } catch (error) {
    retValue = Promise.reject(error);
  }
  return retValue;
}
export function renderLink(str, isRunSanitizeHtml, isAgent, isLeLink = !isAgent) {
  // isLeLink defines if className 'le-link' will be used - (default blue link styling)
  if (str && isString(str)) {
    let msg = str;
    if (isRunSanitizeHtml) {
      msg = HtmlSanitizerUtils.sanitizeHtml(msg).cleanText;
    }
    const className = isLeLink ? 'le-link' : 'le-markdown-link';
    if (!isAgent) {
      msg = replaceSpecificElement(msg, 'a', 'span');
    }

    if (!isHasLink(msg)) {
      return msg;
    }

    return linkifyHtml(msg, {
      defaultProtocol: 'https', target: '_blank', rel: 'noopener noreferrer', className: `link ${className}`, attributes: { 'aria-describedby': 'opens-in-a-new-window' },
    });
  }
  return str;
}
export const isHasLink = str => isString(str) && Boolean(linkify.find(str).length);

export function getLogger(context) {
  return Logger.getLogger(context, 'AgentWorkspace');
}

export function isAccountHasChat() {
  const isChatAccount = sessionManager.getFeaturePropertyState(FEATURES.IS_CHAT_ENABLE);
  const isEnforce = sessionManager.getFeaturePropertyState(FEATURES.ENFORCE_NEW_AGENT_WORKSPACE);
  return isChatAccount || !isEnforce;
}

export function sendErrorLogs(logger, namespace, message, payload) {
  const getSessionValue = store.getters['session/getValue'];
  const accountId = getSessionValue('accountId');
  const agentId = getSessionValue('userId');
  const loginName = getSessionValue('loginName');
  const phase = get(window, ['leConfiguration', 'lp_phase']);
  const environment = get(window, ['leConfiguration', 'lp_grid']);
  const finalNamespace = `${NAMESPACE.NEW_AGENT_WORKSPACE}: ${namespace}`;
  const data = {
    clientTime: getLoggerClientTime(),
    accountId,
    agentId,
    loginName,
    phase,
    environment,
    moduleVersion: `${pkg.version}`,
    data: {
      ...payload,
    },
  };

  logger.error(message, finalNamespace, data);
}

export function sendErrorLogsCallback(namespace, message, payload) {
  const logger = getLogger(namespace);
  sendErrorLogs(logger, namespace, message, payload);
}

export function sendPendoEvents(eventName, loadedBy = '') {
  sendPendoEvents2(eventName, {
    ...(loadedBy?.length && { loadedBy }),
  });
}

// The original function is not flexible enough and we don't want to change all the files that are using it
export function sendPendoEvents2(eventName, props = {}) {
  const getSessionValue = store.getters['session/getValue'];

  window.pendo?.track(eventName, {
    userId: getSessionValue('userId'),
    accountId: getSessionValue('accountId'),
    productArea: PENDO_PRODUCT_AREA,
    ...props,
  });
}

export function moveCursorToEnd(event) {
  /**
   * This function ensures the cursor in a text input remains at the end of the text after actions that might cause it to move to the beginning.
   */
  const textRange = document.createRange();
  const selection = window.getSelection();
  textRange.selectNodeContents(event.target);
  textRange.collapse(false);
  selection.removeAllRanges();
  selection.addRange(textRange);
}

// Render a single element
export const renderEl = (tag, ops) => Object.assign(document.createElement(tag), ops);

// Waits an arbitrary amount of time before resolving the returned Promise
export const waiter = (ms = 0) => {
  return Number.isFinite(ms) && ms >= 0
    ? new Promise(r => setTimeout(r, ms))
    : Promise.reject(`Timeout [${ms}] is negative or not a number`);
};

export const recommendationHashId = (() => {
  const cache = {};

  return function hashString(str) {
      if (cache[str]) {
          return cache[str];
      }

      let hash = 0;
      for (let i = 0; i < str.length; i++) {
          hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
      }

      const hashKey = hash.toString(16);
      cache[str] = hashKey;
      return hashKey;
  };
})();

export const sortByDate = (array, key) =>
  [...array].sort((a, b) => {
    const timeA = moment.utc(a[key]);
    const timeB = moment.utc(b[key]);

    return timeB.diff(timeA);
  });
